// Pure helpers for the director dashboard: exact decimal math on money strings
// and multi-region merging per CONTRACT.md §4. No network / keycloak imports here
// so the module is unit-testable in isolation.
import {
  AggregatedMoney,
  AggregatedMonth,
  AggregatedProduct,
  AggregatedProducts,
  AggregatedSummary,
  AggregateResult,
  AnalyticsMeta,
  AnalyticsMoney,
  AnalyticsMonthly,
  AnalyticsProducts,
  AnalyticsReceivableClient,
  AnalyticsReceivables,
  AnalyticsStaffCredits,
  AggregatedStaffCredits,
  AnalyticsSummary,
  AnalyticsTopClient,
  AnalyticsTopClients,
  DecimalString,
  RegionDegraded,
  RegionFailure,
  WithRegion,
} from './types/analytics';

// ---------------------------------------------------------------------------
// Decimal strings (never floats)
// ---------------------------------------------------------------------------

const DECIMAL_RE = /^([+-])?(\d*)(?:\.(\d*))?$/;
const TEN = BigInt(10);
const ZERO = BigInt(0);

interface ParsedDecimal {
  units: bigint; // value * 10^scale
  scale: number;
}

const parseDecimal = (value: DecimalString | number | null | undefined): ParsedDecimal => {
  if (value === null || value === undefined || value === '') return { units: ZERO, scale: 0 };
  const str = String(value).trim();
  const m = DECIMAL_RE.exec(str);
  if (!m || (m[2] === '' && (m[3] === undefined || m[3] === ''))) {
    throw new Error(`Invalid decimal string: "${str}"`);
  }
  const sign = m[1] === '-' ? -1 : 1;
  const intPart = m[2] || '0';
  const fracPart = m[3] || '';
  const units = BigInt(intPart + fracPart) * BigInt(sign);
  return { units, scale: fracPart.length };
};

const rescale = (d: ParsedDecimal, scale: number): bigint =>
  d.units * TEN ** BigInt(scale - d.scale);

const formatUnits = (units: bigint, scale: number, minScale = 2): string => {
  // pad to at least minScale fraction digits
  let s = scale;
  let u = units;
  if (s < minScale) {
    u = u * TEN ** BigInt(minScale - s);
    s = minScale;
  }
  const negative = u < ZERO;
  const abs = (negative ? -u : u).toString().padStart(s + 1, '0');
  const intPart = abs.slice(0, abs.length - s);
  const fracPart = abs.slice(abs.length - s);
  return `${negative ? '-' : ''}${intPart}${s > 0 ? `.${fracPart}` : ''}`;
};

/** Exact sum of decimal strings. Result keeps the max scale seen (min 2 digits). */
export const addDecimals = (...values: Array<DecimalString | null | undefined>): DecimalString => {
  const parsed = values.map(parseDecimal);
  const scale = parsed.reduce((acc, d) => Math.max(acc, d.scale), 0);
  const total = parsed.reduce((acc, d) => acc + rescale(d, scale), ZERO);
  return formatUnits(total, scale);
};

/** Compare two decimal strings: -1, 0, 1. */
export const compareDecimals = (a: DecimalString, b: DecimalString): number => {
  const pa = parseDecimal(a);
  const pb = parseDecimal(b);
  const scale = Math.max(pa.scale, pb.scale);
  const ua = rescale(pa, scale);
  const ub = rescale(pb, scale);
  return ua === ub ? 0 : ua > ub ? 1 : -1;
};

/** Exact ratio of two integers as decimal string with `digits` fraction digits (half-up). */
export const ratioString = (numerator: number, denominator: number, digits = 4): DecimalString => {
  if (!denominator) return formatUnits(ZERO, digits, digits);
  const factor = TEN ** BigInt(digits);
  const n = BigInt(numerator) * factor * BigInt(2);
  const d = BigInt(denominator);
  const units = (n / d + BigInt(1)) / BigInt(2); // half-up for non-negative values
  return formatUnits(units, digits, digits);
};

/**
 * Lossy conversion for *display only* (chart axes, % deltas).
 * Money math must use addDecimals.
 */
export const decimalToNumber = (value: DecimalString | null | undefined): number => {
  if (value === null || value === undefined || value === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Sum USD amounts where some may be null (region without FX rate) or undefined (field
 * not sent by an older backend → treated as 0). Nulls are NOT counted as 0: the result
 * is the sum of known parts with `incomplete: true`, or null when nothing is known.
 */
export const sumUsd = (
  values: Array<DecimalString | null | undefined>,
): { value: DecimalString | null; incomplete: boolean } => {
  const known = values.filter((v): v is DecimalString => v !== null && v !== undefined && v !== '');
  const hasNull = values.some((v) => v === null);
  if (!known.length) {
    return hasNull ? { value: null, incomplete: true } : { value: addDecimals(), incomplete: false };
  }
  return { value: addDecimals(...known), incomplete: hasNull };
};

const usdOrNull = (...values: Array<DecimalString | null | undefined>) => sumUsd(values).value;

// ---------------------------------------------------------------------------
// Region targets
// ---------------------------------------------------------------------------

export interface RegionTarget {
  region: string;
  url: string;
}

export interface RegionTargets {
  targets: RegionTarget[];
  /** region -> region whose backend URL it shares (skipped to avoid double counting). */
  aliases: Record<string, string>;
  /** regions without a configured backend URL. */
  missing: string[];
}

const normalizeUrl = (url: string) => {
  const trimmed = url.trim();
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
};

/**
 * Resolve selected regions to backend URLs, deduplicating by URL so that dev
 * environments, where several regions point to one backend, are not double counted.
 */
export const resolveRegionTargets = (
  regions: string[],
  urls: Record<string, string | undefined>,
): RegionTargets => {
  const seen = new Map<string, string>();
  const result: RegionTargets = { targets: [], aliases: {}, missing: [] };
  regions.forEach((region) => {
    const raw = urls[region];
    if (!raw) {
      result.missing.push(region);
      return;
    }
    const url = normalizeUrl(raw);
    const owner = seen.get(url.toLowerCase());
    if (owner) {
      result.aliases[region] = owner;
      return;
    }
    seen.set(url.toLowerCase(), region);
    result.targets.push({ region, url });
  });
  return result;
};

// ---------------------------------------------------------------------------
// Merging
// ---------------------------------------------------------------------------

export type RegionResponses<T> = Array<{ region: string; data: T }>;

const money = (
  items: Array<{ m: AnalyticsMoney | undefined; currency: string }>,
): AggregatedMoney => {
  const { value: amount_usd, incomplete } = sumUsd(items.map((i) => i.m?.amount_usd));
  const extra = incomplete ? { incomplete: true } : {};
  if (items.length === 1) {
    return { amount_usd, amount: addDecimals(items[0].m?.amount), currency: items[0].currency, ...extra };
  }
  return { amount_usd, ...extra };
};

const sumCounts = (values: Array<number | null | undefined>) =>
  values.reduce<number>((acc, v) => acc + (v || 0), 0);

export const mergeSummaries = (responses: RegionResponses<AnalyticsSummary>): AggregatedSummary | null => {
  if (!responses.length) return null;
  const first = responses[0].data;
  const pick = (f: (s: AnalyticsSummary) => AnalyticsMoney | undefined) =>
    money(responses.map((r) => ({ m: f(r.data), currency: r.data.meta?.base_currency })));
  const count = (f: (s: AnalyticsSummary) => number | undefined) => sumCounts(responses.map((r) => f(r.data)));

  return {
    mrr: { ...pick((s) => s.mrr), month: first.mrr?.month },
    mrr_prev: { ...pick((s) => s.mrr_prev), month: first.mrr_prev?.month },
    current_month_estimate: {
      ...pick((s) => s.current_month_estimate),
      month: first.current_month_estimate?.month,
    },
    cash_in_30d: pick((s) => s.cash_in_30d),
    cash_in_prev_30d: pick((s) => s.cash_in_prev_30d),
    cash_in_30d_breakdown: {
      gateway: pick((s) => s.cash_in_30d_breakdown?.gateway),
      external: pick((s) => s.cash_in_30d_breakdown?.external),
      refunds: pick((s) => s.cash_in_30d_breakdown?.refunds),
    },
    staff_credits_30d: {
      added: pick((s) => s.staff_credits_30d?.added),
      removed: pick((s) => s.staff_credits_30d?.removed),
      net: pick((s) => s.staff_credits_30d?.net),
      operations: count((s) => s.staff_credits_30d?.operations),
      clients: count((s) => s.staff_credits_30d?.clients),
    },
    promo_credits_30d: pick((s) => s.promo_credits_30d),
    paying_clients: { count: count((s) => s.paying_clients?.count), month: first.paying_clients?.month },
    paying_clients_prev: {
      count: count((s) => s.paying_clients_prev?.count),
      month: first.paying_clients_prev?.month,
    },
    new_clients_30d: count((s) => s.new_clients_30d),
    new_paying_clients_30d: count((s) => s.new_paying_clients_30d),
    churned_clients: { count: count((s) => s.churned_clients?.count), month: first.churned_clients?.month },
    receivables: {
      ...pick((s) => s.receivables),
      unpaid_invoices: count((s) => s.receivables?.unpaid_invoices),
      negative_balance_clients: count((s) => s.receivables?.negative_balance_clients),
    },
    clients_total: count((s) => s.clients_total),
    clients_active: count((s) => s.clients_active),
  };
};

export const mergeMonthly = (responses: RegionResponses<AnalyticsMonthly>): AggregatedMonth[] => {
  const byMonth = new Map<string, AggregatedMonth>();
  responses.forEach(({ data }) => {
    (data.months || []).forEach((m) => {
      const prev = byMonth.get(m.month);
      if (!prev) {
        byMonth.set(m.month, {
          month: m.month,
          partial: !!m.partial,
          incomplete: hasNullUsd(m),
          cash_in_usd: usdOrNull(m.cash_in?.amount_usd),
          refunds_usd: usdOrNull(m.refunds?.amount_usd),
          cash_in_gateway_usd: usdOrNull(m.cash_in_gateway?.amount_usd),
          cash_in_external_usd: usdOrNull(m.cash_in_external?.amount_usd),
          staff_credits_net_usd: usdOrNull(m.staff_credits_net?.amount_usd),
          promo_credits_usd: usdOrNull(m.promo_credits?.amount_usd),
          billed_revenue_usd: usdOrNull(m.billed_revenue?.amount_usd),
          new_clients: m.new_clients || 0,
          new_paying_clients: m.new_paying_clients || 0,
          paying_clients: m.paying_clients || 0,
          churned_clients: m.churned_clients || 0,
          cohort_converted: m.cohort_converted || 0,
          cohort_conversion_rate: '0.0000',
        });
        return;
      }
      prev.partial = prev.partial || !!m.partial;
      prev.incomplete = prev.incomplete || hasNullUsd(m);
      prev.cash_in_usd = usdOrNull(prev.cash_in_usd, m.cash_in?.amount_usd);
      prev.refunds_usd = usdOrNull(prev.refunds_usd, m.refunds?.amount_usd);
      prev.cash_in_gateway_usd = usdOrNull(prev.cash_in_gateway_usd, m.cash_in_gateway?.amount_usd);
      prev.cash_in_external_usd = usdOrNull(prev.cash_in_external_usd, m.cash_in_external?.amount_usd);
      prev.staff_credits_net_usd = usdOrNull(prev.staff_credits_net_usd, m.staff_credits_net?.amount_usd);
      prev.promo_credits_usd = usdOrNull(prev.promo_credits_usd, m.promo_credits?.amount_usd);
      prev.billed_revenue_usd = usdOrNull(prev.billed_revenue_usd, m.billed_revenue?.amount_usd);
      prev.new_clients += m.new_clients || 0;
      prev.new_paying_clients += m.new_paying_clients || 0;
      prev.paying_clients += m.paying_clients || 0;
      prev.churned_clients += m.churned_clients || 0;
      prev.cohort_converted += m.cohort_converted || 0;
    });
  });
  return Array.from(byMonth.values())
    .map((m) => ({ ...m, cohort_conversion_rate: ratioString(m.cohort_converted, m.new_clients, 4) }))
    .sort((a, b) => (a.month < b.month ? -1 : a.month > b.month ? 1 : 0));
};

export const mergeProducts = (responses: RegionResponses<AnalyticsProducts>): AggregatedProducts => {
  const byKey = new Map<string, AggregatedProduct>();
  responses.forEach(({ region, data }) => {
    (data.products || []).forEach((p) => {
      const key = `${p.product_type}::${(p.name || '').trim().toLowerCase()}`;
      const prev = byKey.get(key);
      if (!prev) {
        byKey.set(key, {
          key,
          name: p.name,
          product_type: p.product_type,
          billed_revenue_usd: usdOrNull(p.billed_revenue?.amount_usd),
          clients: p.clients || 0,
          regions: [region],
          incomplete: p.billed_revenue?.amount_usd === null,
        });
        return;
      }
      prev.billed_revenue_usd = usdOrNull(prev.billed_revenue_usd, p.billed_revenue?.amount_usd);
      prev.incomplete = prev.incomplete || p.billed_revenue?.amount_usd === null;
      prev.clients += p.clients || 0;
      if (!prev.regions.includes(region)) prev.regions.push(region);
    });
  });
  const products = Array.from(byKey.values()).sort((a, b) =>
    compareUsdDescending(a.billed_revenue_usd, b.billed_revenue_usd),
  );
  return {
    incomplete: responses.some((r) => hasNullUsd(r.data)),
    products,
    included: {
      bare_metal_usd: usdOrNull(...responses.map((r) => r.data.included?.bare_metal?.amount_usd)),
      gpu_usd: usdOrNull(...responses.map((r) => r.data.included?.gpu?.amount_usd)),
    },
    total_usd: usdOrNull(...responses.map((r) => r.data.total?.amount_usd)),
  };
};

const compareUsdDescending = (a: DecimalString | null | undefined, b: DecimalString | null | undefined): number => {
  if (a == null) return b == null ? 0 : 1;
  if (b == null) return -1;
  return compareDecimals(b, a);
};

const mergeTagged = <T>(
  lists: Array<{ region: string; baseCurrency: string; items: T[] }>,
  usdOf: (item: T) => DecimalString | null | undefined,
  limit?: number,
): Array<WithRegion<T>> => {
  const all: Array<WithRegion<T>> = [];
  lists.forEach(({ region, baseCurrency, items }) =>
    (items || []).forEach((item) => all.push({ ...item, region, baseCurrency })));
  all.sort((a, b) => compareUsdDescending(usdOf(a), usdOf(b)));
  return typeof limit === 'number' ? all.slice(0, limit) : all;
};

export const mergeTopClients = (
  responses: RegionResponses<AnalyticsTopClients>,
  limit?: number,
): Array<WithRegion<AnalyticsTopClient>> =>
  mergeTagged(
    responses.map((r) => ({ region: r.region, baseCurrency: r.data.meta.base_currency, items: r.data.clients })),
    (c) => c.billed_revenue?.amount_usd,
    limit,
  );

export const mergeReceivables = (
  responses: RegionResponses<AnalyticsReceivables>,
  limit?: number,
): { total_usd: DecimalString | null; clients: Array<WithRegion<AnalyticsReceivableClient>> } => ({
  total_usd: usdOrNull(...responses.map((r) => r.data.total?.amount_usd)),
  clients: mergeTagged(
    responses.map((r) => ({ region: r.region, baseCurrency: r.data.meta.base_currency, items: r.data.clients })),
    (c) => c.total_due?.amount_usd,
    limit,
  ),
});

export const mergeStaffCredits = (
  responses: RegionResponses<AnalyticsStaffCredits>,
  limit?: number,
): AggregatedStaffCredits => {
  const byStaff: AggregatedStaffCredits['by_staff'] = [];
  const operations: AggregatedStaffCredits['operations'] = [];
  responses.forEach(({ region, data }) => {
    (data.by_staff || []).forEach((s) => byStaff.push({ ...s, region }));
    (data.operations || []).forEach((o) => operations.push({ ...o, region }));
  });
  byStaff.sort((a, b) => compareUsdDescending(a.added?.amount_usd, b.added?.amount_usd));
  // newest first; ISO 8601 UTC strings compare lexicographically
  operations.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  return {
    incomplete: responses.some((r) => hasNullUsd(r.data)),
    totals: {
      added_usd: usdOrNull(...responses.map((r) => r.data.totals?.added?.amount_usd)),
      removed_usd: usdOrNull(...responses.map((r) => r.data.totals?.removed?.amount_usd)),
      net_usd: usdOrNull(...responses.map((r) => r.data.totals?.net?.amount_usd)),
      external_usd: usdOrNull(...responses.map((r) => r.data.totals?.external?.amount_usd)),
    },
    by_staff: byStaff,
    operations: typeof limit === 'number' ? operations.slice(0, limit) : operations,
  };
};

/** Percent change (display-only number) or null when previous is zero. */
export const percentDelta = (
  current: DecimalString | number | null,
  previous: DecimalString | number | null,
): number | null => {
  if (current === null || previous === null) return null;
  const c = typeof current === 'number' ? current : decimalToNumber(current);
  const p = typeof previous === 'number' ? previous : decimalToNumber(previous);
  if (!p) return null;
  return ((c - p) / Math.abs(p)) * 100;
};

// ---------------------------------------------------------------------------
// Parallel fetch across regions
// ---------------------------------------------------------------------------

/** True if any `amount_usd` anywhere in the payload is null. */
export const hasNullUsd = (value: unknown, depth = 0): boolean => {
  if (!value || typeof value !== 'object' || depth > 8) return false;
  if (Array.isArray(value)) return value.some((v) => hasNullUsd(v, depth + 1));
  const obj = value as Record<string, unknown>;
  if ('amount_usd' in obj && obj.amount_usd === null) return true;
  return Object.keys(obj).some((k) => k !== 'meta' && hasNullUsd(obj[k], depth + 1));
};

/** Region is degraded if it reports unknown FX currencies or returns null USD amounts. */
export const detectDegraded = (region: string, data: { meta?: AnalyticsMeta }): RegionDegraded | null => {
  const currencies = data?.meta?.fx_unknown_currencies || [];
  if (currencies.length || hasNullUsd(data)) return { region, currencies: [...currencies] };
  return null;
};

/** Union degraded entries by region (currencies merged). */
export const mergeDegraded = (lists: RegionDegraded[][]): RegionDegraded[] => {
  const map = new Map<string, Set<string>>();
  lists.flat().forEach((d) => {
    const set = map.get(d.region) || new Set<string>();
    d.currencies.forEach((c) => set.add(c));
    map.set(d.region, set);
  });
  return Array.from(map, ([region, set]) => ({ region, currencies: Array.from(set).sort() }));
};

const failureDetails = (error: unknown): Pick<RegionFailure, 'error' | 'status'> => {
  const e = error as { response?: { status?: number }; code?: string };
  const status = e?.response?.status;
  if (typeof status === 'number') return { error: `HTTP ${status}`, status };
  // Do not expose server tracebacks, credentials, or arbitrary response bodies in the UI.
  if (e?.code === 'ECONNABORTED' || e?.code === 'ETIMEDOUT') return { error: 'timeout' };
  return { error: error instanceof Error ? 'invalid or unavailable response' : 'unavailable' };
};

/**
 * Fetch one endpoint from all selected regions in parallel (Promise.allSettled),
 * dedupe regions sharing a backend URL, merge successful responses and report
 * failed / unconfigured regions (CONTRACT.md §4).
 */
export const fetchAcrossRegions = async <T extends { meta: AnalyticsMeta }, R>(
  regions: string[],
  fetcher: (region: string) => Promise<{ data: T }>,
  merge: (responses: RegionResponses<T>) => R,
  urls: Record<string, string | undefined>,
): Promise<AggregateResult<R>> => {
  const { targets, missing } = resolveRegionTargets(regions, urls);
  const settled = await Promise.allSettled(targets.map((t) => Promise.resolve().then(() => fetcher(t.region))));
  const ok: RegionResponses<T> = [];
  const failedRegions: RegionFailure[] = missing.map((region) => ({ region, error: 'not configured' }));
  const degradedRegions: RegionDegraded[] = [];
  const metas: Record<string, AnalyticsMeta> = {};
  settled.forEach((res, i) => {
    const region = targets[i].region;
    if (res.status === 'fulfilled' && res.value?.data) {
      try {
        const data = res.value.data;
        if (!data.meta?.base_currency || !data.meta.generated_at) throw new Error('Invalid analytics metadata');
        // Validate each region independently so malformed money cannot discard healthy regions.
        merge([{ region, data }]);
        ok.push({ region, data });
        metas[region] = data.meta;
        const degraded = detectDegraded(region, data);
        if (degraded) degradedRegions.push(degraded);
      } catch (error: unknown) {
        failedRegions.push({ region, ...failureDetails(error) });
      }
    } else {
      const reason: unknown = res.status === 'rejected' ? res.reason : new Error('empty response');
      failedRegions.push({ region, ...failureDetails(reason) });
    }
  });
  return {
    data: ok.length ? merge(ok) : null,
    okRegions: ok.map((r) => r.region),
    failedRegions,
    degradedRegions,
    metas,
  };
};
